import type { ReactNode } from 'react'
import { PartialMark, Text, VerdictTag } from '@grovus/design-system'
import type { Dict, Lang } from '../i18n'
import type {
  AccountRef, Benchmarks, CreativeVerdict, DeliveryCheck, Dimension, Measure, VerdictBasis, VerdictKey, VerdictMetric,
  VerdictReason, VerdictReference,
} from './contract'
import type { ConfidenceThresholds } from './confidence'
import { NO_VALUE, fill, money, num, percent, plural, ratio } from './format'
import { dayMonth } from '../share/date'
import { belowFloor, type Row, type SortLabels } from './partition'

/**
 * What a reason sentence needs beyond the row itself. Held together rather than threaded
 * through every cell, because the same context formats the table, the grid and the tiles.
 */
export interface ViewContext {
  t: Dict
  lang: Lang
  currency: string | null
  benchmarks: Benchmarks
  thresholds: ConfidenceThresholds
  periodDays: number
  dimensions: { hypotheses: Dimension[]; techniques: Dimension[]; messages: Dimension[]; offers: Dimension[]; audiences: Dimension[] }
  accounts: AccountRef[]
  /**
   * Whether the accounts these rows came from are the demo ones. Presentation only: it
   * places the account mark and the marked definition, and nothing reads it as access.
   */
  isDemo: boolean
  /**
   * The response-level basis of the verdicts (GRO-185). A row with no reference reads its
   * metric here; absent or `null`, from a server before GRO-185, it reads as ROAS.
   */
  basis?: VerdictBasis | null
}

/**
 * A dimension value in the active language.
 *
 * The labels travel with the response, so a value the server knows about always renders in
 * the reader's language and the frontend carries no translation map that could fall behind
 * the taxonomy.
 */
export function labelOf(values: Dimension[], id: string | null, lang: Lang): string | null {
  if (id === null) return null
  return values.find((value) => value.id === id)?.label[lang] ?? id
}

export function conversionUnit(count: number, ctx: ViewContext): string {
  return plural(count, ctx.lang, {
    one: ctx.t.unitOne,
    few: ctx.t.unitFew,
    many: ctx.t.unitMany,
  })
}

/**
 * One label per format value (GRO-174), so the column, the filter, both badges and the panel
 * cannot print one value under two names.
 */
export function formatLabel(format: Row['format'], t: Dict): string {
  return { carousel: t.carousel, video: t.video, image: t.image }[format]
}

/**
 * The Audience cell (GRO-175): empty with no audience, the label of the one there is, and
 * the mixed label when the creative's ads reach several. Mixed is never a scope value.
 */
export function audienceCell(row: Row, audiences: Dimension[], lang: Lang, t: Dict): string {
  if (row.audienceIds.length === 0) return ''
  if (row.audienceIds.length > 1) return t.audienceMixed
  return labelOf(audiences, row.audienceIds[0], lang) ?? ''
}

/** The labels the table, the grid and the screen sort the text columns by, one set for all three. */
export function sortLabels(ctx: ViewContext): SortLabels {
  return {
    audience: (row) => audienceCell(row, ctx.dimensions.audiences, ctx.lang, ctx.t),
    hypothesis: (row) => labelOf(ctx.dimensions.hypotheses, row.hypothesisId, ctx.lang),
    technique: (row) => labelOf(ctx.dimensions.techniques, row.techniqueId, ctx.lang),
  }
}

export function verdictLabel(key: VerdictKey, t: Dict): string {
  return {
    scale: t.vlScale,
    hold: t.vlHold,
    watch: t.vlWatch,
    rework: t.vlRework,
    kill: t.vlKill,
    insufficient: t.vlInsufficient,
    notStarted: t.vlNotStarted,
  }[key]
}

function checkName(measure: DeliveryCheck['measure'], t: Dict): string {
  return { ctr: t.vqCtr, hookRate: t.vqHook }[measure]
}

export interface VerdictQualifier {
  text: string
  /** `paren` reads «Отключить (CTR, хук)», `dot` reads «Данных мало · нет истории конверсий». */
  separator: 'paren' | 'dot'
}

/**
 * The qualifier beside the verdict label. A weak-delivery Kill names the measures of its
 * failed checks; a missing reference with no conversions in 90 days says so. Only the
 * `noConversions` cause claims no conversion history: the other causes leave conversions
 * the sums could not see, so they get no qualifier.
 */
export function verdictQualifier(row: Row, t: Dict): VerdictQualifier | null {
  const { verdict } = row
  if (verdict === null) return null
  if (verdict.reason === 'noReference' && verdict.referenceMissing === 'noConversions') {
    return { text: t.vqNoHistory, separator: 'dot' }
  }
  if (verdict.reason !== 'worseDeliveryWeak') return null
  const failed = verdict.checks.filter((check) => check.status === 'fail')
  if (failed.length === 0) return null
  return { text: failed.map((check) => checkName(check.measure, t)).join(', '), separator: 'paren' }
}

/** The qualifier as one string beside its label, for the CSV. */
export function qualifiedLabel(label: string, qualifier: VerdictQualifier | null): string {
  if (qualifier === null) return label
  return qualifier.separator === 'dot' ? `${label} · ${qualifier.text}` : `${label} (${qualifier.text})`
}

/** The qualifier text as a screen renders it beside the label. */
export function qualifierText(qualifier: VerdictQualifier): string {
  return qualifier.separator === 'dot' ? `· ${qualifier.text}` : qualifier.text
}

const OUTCOME_MEASURES: readonly Measure[] = ['spend', 'conversions', 'conversionValue']

export function measureNames(row: Row, ctx: ViewContext): string {
  const names: Record<Measure, string> = {
    spend: ctx.t.spend,
    impressions: ctx.t.impressions,
    clicks: ctx.t.clicks.replace('%n', '').trim(),
    conversions: ctx.t.conversions,
    conversionValue: ctx.t.purchValue,
    threeSecondViews: ctx.t.hookCol,
    thruPlays: ctx.t.holdCol,
  }
  // A measure no ad reported at all is absent from `incomplete` - "incomplete" means some
  // ads reported it - but the panel's completeness copy has to name it all the same, so
  // null-valued outcome measures join the list.
  const absent = OUTCOME_MEASURES.filter(
    (measure) => row.metrics[measure] === null && !row.metrics.incomplete.includes(measure),
  )
  const listed = [...row.metrics.incomplete, ...absent].filter((measure) =>
    OUTCOME_MEASURES.includes(measure),
  )
  return listed.map((measure) => names[measure]).join(', ')
}

/** The metric a verdict reads in: its reference's, else the response's basis, else ROAS. */
export function verdictMetric(verdict: CreativeVerdict | null, basis: VerdictBasis | null | undefined): VerdictMetric {
  return verdict?.reference?.metric ?? basis?.metric ?? 'roas'
}

export function metricName(metric: VerdictMetric, t: Dict): string {
  return metric === 'cpa' ? t.cpaCol : t.roasCol
}

/**
 * What a verdict compares against, without its value: the target, the 90-day average, or
 * the 90-day average of a scope outside the reporting currency, named with that currency.
 * A null reference reads as the 90-day average.
 */
export function referenceNoun(reference: VerdictReference | null, t: Dict): string {
  if (reference?.kind === 'target') return t.vrRefTarget
  if (reference?.outsideReporting) return fill(t.vrRefAverageIn, { cur: reference.currency })
  return t.vrRefAverage
}

/**
 * The served reason, in the active language.
 *
 * Every figure comes from the served verdict or the row: the gap, the required gap, the
 * reference and the checks are the server's, so the sentence cannot say something the
 * verdict did not decide. A null verdict has no sentence.
 */
export function reasonSentence(row: Row, ctx: ViewContext): string {
  const { verdict } = row
  if (verdict === null) return ''
  const { t, lang, thresholds } = ctx
  // CPA is money in the reference's currency; ROAS is a ratio (GRO-185).
  const metric = verdictMetric(verdict, ctx.basis)
  const currency = verdict.reference?.currency ?? ctx.currency
  const value = (figure: number | null) => (metric === 'cpa' ? money(figure, currency, lang, 2) : ratio(figure, lang))
  const reference = value(verdict.reference?.value ?? null)
  const noun = referenceNoun(verdict.reference, t)
  const template: Record<VerdictReason, string> = {
    better: t.vrBetter,
    within: t.vrWithin,
    worseDeliveryFine: t.vrWorseDeliveryFine,
    worseDeliveryWeak: t.vrWorseDeliveryWeak,
    deliveryUnchecked: t.vrDeliveryUnchecked,
    belowProvisional: t.vrBelowProvisional,
    belowDirection: t.vrBelowDirection,
    noReference:
      verdict.referenceMissing !== 'noConversions'
        ? t.vrNoReference
        : metric === 'cpa' ? t.vrNoConversionsCpa : t.vrNoConversions,
    stopLoss: t.vrStopLoss,
    noRoas: t.vrNoRoas,
    noCpa: t.vrNoCpa,
    dataIncomplete: t.vrDataIncomplete,
    neverDelivered: t.vrNeverDelivered,
  }
  // A weak-delivery Kill names the checks that failed; the other sentences name every check
  // the server could run.
  const named = verdict.reason === 'worseDeliveryWeak' ? ['fail'] : ['pass', 'fail']
  const checks = verdict.checks
    .filter((check) => named.includes(check.status))
    .map((check) =>
      fill(t.vcCheck, {
        name: checkName(check.measure, t),
        v: percent(check.value, lang),
        bm: percent(check.benchmark, lang),
      }),
    )
    .join(', ')
  return fill(template[verdict.reason], {
    n: verdict.n,
    u: conversionUnit(verdict.n, ctx),
    p: verdict.gap === null ? null : num(Math.abs(verdict.gap), lang, 1),
    b: verdict.requiredGap === null ? null : num(verdict.requiredGap, lang, 1),
    metric: metricName(metric, t),
    r: value(metric === 'cpa' ? row.derived.cpa.value : row.derived.roas.value),
    ar: reference,
    ref: `${noun} (${reference})`,
    refv: `${noun} ${reference}`,
    bound: metric === 'cpa' ? t.vrBoundCpa : t.vrBoundRoas,
    lo: thresholds.provisionalAt,
    dir: thresholds.directionAt,
    checks,
    m: measureNames(row, ctx) || t.conversions,
    s: verdict.stopLoss === null ? null : money(verdict.stopLoss.spend, verdict.reference?.currency ?? ctx.currency, lang, 2),
    th: verdict.stopLoss === null ? null : money(verdict.stopLoss.threshold, verdict.reference?.currency ?? ctx.currency, lang, 2),
    bc: verdict.stopLoss === null ? null : value(verdict.stopLoss.bestCase),
    cur: ctx.currency,
  })
}

/**
 * A figure that does not cover everything: a measure only some ads reported, an
 * attribution mix, or a scope the server could not wholly see.
 *
 * Warn-toned and marked rather than hidden: the number is real as far as it goes, and a
 * screen that dropped it would be answering a different question than the one asked. The
 * glyph is explained in words by the `PartialLegend` of the surface that renders it.
 */
export function Partial({ t }: { t: Dict }) {
  return <PartialMark variant="compact" label={t.partialWord} name={t.partialMeasure} />
}

/** A value with the partial mark when its measure is incomplete. */
export function marked(value: ReactNode, partial: boolean, t: Dict): ReactNode {
  if (!partial) return value
  return (
    <>
      {value}
      <Partial t={t} />
    </>
  )
}

export type MarkKind = 'measure' | 'lagging'

/**
 * What a surface's legend has to explain, recorded while the surface builds its cells.
 *
 * The legend owner builds every cell through `at(key)` - which renders exactly what
 * `marked()` renders - by plain function calls in its own render, and only then reads
 * `kinds` with the keys it actually shows. No descendant writes here, so the legend never
 * depends on render order, and a mark in a hidden column is built but never counted.
 */
export interface MarkTally {
  at: (key: string) => (value: ReactNode, partial: boolean) => ReactNode
  lagging: (key: string, row: Row, ctx: ViewContext) => void
  /** The lagging mark for one account the caller renders itself, recorded when shown. */
  account: (key: string, lagging: boolean) => ReactNode
  /** The kinds recorded under `keys`, or under every key when none are given. */
  kinds: (keys?: readonly string[]) => ReadonlySet<MarkKind>
}

export function markTally(t: Dict): MarkTally {
  const seen = new Map<string, Set<MarkKind>>()
  const record = (key: string, kind: MarkKind) => {
    const set = seen.get(key) ?? new Set<MarkKind>()
    set.add(kind)
    seen.set(key, set)
  }
  return {
    at: (key) => (value, partial) => {
      if (partial) record(key, 'measure')
      return marked(value, partial, t)
    },
    lagging: (key, row, ctx) => {
      if (laggingAccounts(row, ctx).length > 0) record(key, 'lagging')
    },
    account: (key, lagging) => {
      if (!lagging) return null
      record(key, 'lagging')
      return <PartialMark variant="compact" label={t.partialWord} name={t.partialLong} data-testid="lagging-mark" />
    },
    kinds: (keys) => {
      const out = new Set<MarkKind>()
      for (const [key, set] of seen) {
        if (keys && !keys.includes(key)) continue
        for (const kind of set) out.add(kind)
      }
      return out
    },
  }
}

/**
 * The words for every compact mark on one surface, on the same screen as the marks.
 *
 * A `title` is invisible on a touch device and in a screenshot, so the legend says in
 * words what each mark means. The glyph here is decorative; the text says it.
 */
export function PartialLegend({ kinds, t }: { kinds: ReadonlySet<MarkKind>; t: Dict }) {
  if (kinds.size === 0) return null
  const phrases = [
    kinds.has('measure') ? t.partialLegendMeasure : null,
    kinds.has('lagging') ? t.partialLegendLagging : null,
  ].filter((phrase): phrase is string => phrase !== null)
  return (
    <Text size="sm" className="grv-muted" data-testid="partial-legend">
      <span aria-hidden="true">
        <PartialMark variant="compact" label={t.partialWord} name={t.partialMeasure} data-testid="partial-legend-mark" />
      </span>{' '}
      {phrases.join('; ')}
    </Text>
  )
}

/**
 * A figure on a sample too small to trust: muted, prefixed with a tilde, never struck.
 *
 * A struck-through price reads as an old price with a discount (review 15), so the
 * strike is gone. Since PR #262 a sort places every row by its own value, so the figure
 * is not left out of ranking: it keeps its place and is shown quietly, with the tilde and
 * the sentence saying it is less reliable. The visible `~{children}` sits in an
 * `aria-hidden` span beside a visually-hidden span carrying "{value} - {cannotRank}",
 * which is what keeps the GRO-17 distinctions - a below-threshold figure, a zero and a
 * no-value glyph are announced differently. A generic span takes no accessible name from
 * `aria-label`, so the label is real text content, following the `grv-visually-hidden`
 * pattern the codebase already uses. The `title` keeps the fuller sentence for the mouse.
 * The name `Unrankable` and the `unrankable-value` test id predate #262 and are kept: they
 * are internal, and the contrast and capture scripts select on the id.
 */
/**
 * A figure under the ranking floor: muted with its explanation, as the ROAS sites have done.
 * A null figure stays the plain no-value text, so the mark never decorates a missing value.
 */
export function floorStruck(value: string, present: boolean, row: Row, ctx: ViewContext): ReactNode {
  if (!present || !belowFloor(row, ctx.thresholds)) return value
  return (
    <Unrankable
      title={fill(ctx.t.defStruckBody, { n: ctx.thresholds.directionAt })}
      label={`${value} - ${ctx.t.cannotRank}`}
    >
      {value}
    </Unrankable>
  )
}

/** The served verdict's tag, or the no-value glyph when the row carries none. */
export function VerdictMark({ row, t }: { row: Row; t: Dict }) {
  if (row.verdict === null) return <span data-testid="verdict-none">{NO_VALUE}</span>
  return <VerdictTag verdict={row.verdict.key} label={verdictLabel(row.verdict.key, t)} />
}

export function Unrankable({ children, title, label }: { children: ReactNode; title: string; label: string }) {
  // Muted through the ink token rather than an opacity: dimming ink-8 to 0.62 reads 3.55:1
  // on white, and a figure the reader is meant to notice as uncertain still has to be
  // legible. The tilde and the label carry the meaning; the ink only keeps it quiet.
  return (
    <span className="grv-muted" data-testid="unrankable-value" title={title}>
      <span aria-hidden="true">~{children}</span>{' '}
      <span className="grv-visually-hidden">{label}</span>
    </span>
  )
}

/** The row's accounts whose last successful pull is older than the freshness interval. */
export function laggingAccounts(row: Row, ctx: ViewContext): AccountRef[] {
  const ids = new Set(row.ads.map((ad) => ad.accountId))
  return ctx.accounts.filter((account) => ids.has(account.id) && account.lagging)
}

/** The badges the accounts cell shows before it folds the rest into "+N" (GRO-165). */
/**
 * «на паузе с <date>» under a creative's name, when the server says every in-scope ad of
 * it is paused (GRO-176). Nothing when `at` is null.
 */
export function PausedMark({ at, t, lang }: { at: string | null; t: Dict; lang: Lang }) {
  if (at === null) return null
  return (
    <Text size="xs" className="grv-muted" component="div" data-testid="paused-mark">
      {t.pausedSince(dayMonth(at, lang))}
    </Text>
  )
}

const ACCOUNT_BADGES = 3

/** The accounts a creative ran in, with a mark on any whose ingestion is behind. */
export function AccountMarks({ row, ctx }: { row: Row; ctx: ViewContext }) {
  const lagging = new Set(laggingAccounts(row, ctx).map((account) => account.id))
  const ids = [...new Set(row.ads.map((ad) => ad.accountId))]
  const shortOf = (id: string) => ctx.accounts.find((a) => a.id === id)?.short ?? id
  const shown = ids.length > ACCOUNT_BADGES ? ids.slice(0, ACCOUNT_BADGES) : ids
  const hidden = ids.slice(shown.length)
  return (
    // `nowrap`, since GRO-49: each badge carries a demo glyph on a demo client, and two
    // badges plus their glyphs no longer fit the width auto-layout had settled on, so
    // the cell wrapped to two lines at 1280 and 1440. Held on one line, the column asks
    // the table for the few pixels it needs instead.
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'nowrap' }}>
      {shown.map((id) => {
        const short = shortOf(id)
        return (
          <Text key={id} component="span" size="xs" className={lagging.has(id) ? 'grv-warn' : undefined}>
            {short}
            {/* A single glyph rather than the word: this cell holds one badge per account
                in a twenty-column table, and "Demo" on each of them wraps the column at
                narrow desktop widths. The accessible name carries the meaning, so the
                mark is text to a screen reader and not only a shape. */}
            {ctx.isDemo && (
              <span title={ctx.t.demoMarkLong} data-testid="demo-mark" aria-label={ctx.t.demoMarkLong}>
                {'\u00b0'}
              </span>
            )}
            {lagging.has(id) && (
              <PartialMark variant="compact" label={ctx.t.partialWord} name={ctx.t.partialLong} data-testid="lagging-mark" />
            )}
          </Text>
        )
      })}
      {/* The full list on hover, a lagging account named with the partial word, so the
          folded badges lose nothing; the detail panel lists them too. */}
      {hidden.length > 0 && (
        <Text
          component="span"
          size="xs"
          className={hidden.some((id) => lagging.has(id)) ? 'grv-warn' : undefined}
          title={ids.map((id) => (lagging.has(id) ? `${shortOf(id)} ${ctx.t.partialWord}` : shortOf(id))).join(', ')}
          data-testid="accounts-more"
        >
          +{hidden.length}
        </Text>
      )}
    </span>
  )
}

export const glyph = NO_VALUE

